import { Link } from "react-router-dom";
import { ScanLine, FileText, MapPin, ArrowRight, Coins, Scale, Ban } from "lucide-react";
import Seo from "@/components/Seo";
import { PUBLIC_LAUNCH_STATE_CODE } from "@/lib/displayFlags";
import { getStateByCode, statePath } from "@/lib/stateRegistry";

// Copy follows docs/compliance/01_texas_disclosures.md sections 3, 6 and 17 (Witherspoon-CLO).
// Removed on purpose: "instantly pull every available plan from licensed providers" (overclaims scope,
// wrong terminology), "we don't sell your data" (not verified true), "free, forever", "5-minute switch",
// "save $487" (unsubstantiated).
//
// 2026-09-30 reduced-scope early launch (Owner-approved, Section 17; Reba-CPO product scope doc):
// rewritten entirely. The previous copy described a live plan-shopping and enrollment funnel
// ("See Texas plans" / "Enroll through us") and a live provider-payment relationship ("How we make
// money") -- neither exists today. RateCurrent has zero signed supplier agreements of any kind
// (confirmed fact, not a pending question), so there is no live compensation to describe and no
// live enrollment path to send anyone through. This describes only what the site actually does
// right now: ESID lookup, bill-check, and utility lookup.
const steps = [
  { n: "01", t: "Look up your ESID", d: "Enter your address and we pull your Electric Service ID straight from ERCOT's public registry.", i: ScanLine },
  { n: "02", t: "Check your bill", d: "Upload your bill and we read your rate, usage, and provider from it, then explain it in plain language.", i: FileText },
  { n: "03", t: "Find your utility", d: "See which utility delivers power to your address, and who to call for an outage.", i: MapPin },
];

const notes = [
  {
    i: Coins,
    t: "How we make money",
    d: "RateCurrent has no live compensation arrangement with any energy provider today — we have not signed an agreement with any supplier. ESID lookup and bill-check are free tools, not a funnel toward a paid placement. If that changes, we will update this page.",
  },
  {
    i: Scale,
    t: "What we show",
    d: "ESID results come from ERCOT's own public registry. Bill-check reads the rate, usage, and provider printed on the bill you upload. We do not currently show plan prices, rankings, or savings estimates for any provider.",
  },
  {
    i: Ban,
    t: "What we do not do",
    d: "RateCurrent does not recommend a plan, rank providers, or take payments today. We do not enroll you with anyone. Your provider, not RateCurrent, supplies your service and sends your bills.",
  },
];

export default function HowItWorks() {
  const launchState = getStateByCode(PUBLIC_LAUNCH_STATE_CODE);
  return (
    <div data-testid="how-it-works-page">
      <Seo
        title="How RateCurrent works"
        description="How RateCurrent's ESID lookup and bill-check tools work, and what we do and do not do today."
        path="/how-it-works"
      />
      <section className="bg-[#F7F9FC]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <span className="pill mb-5">How it works</span>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight font-extrabold leading-[1.05] text-[#0A2540]">
              Everything you need to know before you shop for Texas electricity.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="text-lg text-slate-700 leading-relaxed">
              RateCurrent helps you look up your ESID and understand your bill — free tools, no account needed, and no sales pitch.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          {/* Same staggered stair-step offset + hover accent-bar/icon-rotate treatment as
              Home.jsx's step cards -- a deliberate, repeating visual signature threading the
              two pages together rather than two independently-designed sections. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {steps.map((s, i) => (
              <div
                key={i}
                className={`group bg-white rounded-3xl border border-slate-100 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${["", "md:mt-10", "md:mt-4"][i]}`}
                data-testid={`hiw-step-${i}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#DCFCE7] flex items-center justify-center transition-transform duration-300 group-hover:-rotate-6">
                    <s.i className="w-6 h-6 text-[#15803D]" aria-hidden="true" />
                  </div>
                  <span className="font-display font-bold text-3xl text-slate-200">{s.n}</span>
                </div>
                <h2 className="font-display text-xl font-bold tracking-tight text-[#0A2540] mb-2">{s.t}</h2>
                <p className="text-slate-600 leading-relaxed">{s.d}</p>
                <span className="mt-5 block h-[3px] w-8 rounded-full bg-[#16A34A] transition-all duration-300 group-hover:w-14" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-100 bg-[#F7F9FC]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {notes.map((p, i) => (
              <div
                key={i}
                id={i === 0 ? "how-we-are-paid" : undefined}
                className={`group bg-white rounded-3xl border border-slate-100 p-8 scroll-mt-24 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${["", "md:mt-8", "md:mt-3"][i]}`}
                data-testid={`hiw-note-${i}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-[#E8EEF6] flex items-center justify-center mb-6 transition-transform duration-300 group-hover:-rotate-6">
                  <p.i className="w-6 h-6 text-[#0A2540]" aria-hidden="true" />
                </div>
                <h2 className="font-display text-xl font-bold tracking-tight text-[#0A2540]">{p.t}</h2>
                <p className="mt-3 text-slate-600">{p.d}</p>
                <span className="mt-5 block h-[3px] w-8 rounded-full bg-[#16A34A] transition-all duration-300 group-hover:w-14" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="bg-[#0A2540] text-white rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#16A34A] opacity-20 rounded-full blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#16A34A] opacity-10 rounded-full blur-3xl" aria-hidden="true" />
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-tight max-w-[20ch] mx-auto relative">
              Ready to <span className="text-[#4ADE80]">check your account?</span>
            </h2>
            <Link to={launchState ? statePath(launchState.slug, "esid-lookup") : "/"} className="btn-primary mt-10 inline-flex relative" data-testid="hiw-cta">
              Find my ESID <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
